import { createContext, FC, useContext, useState } from 'react';

type Locale = 'en' | 'zh';

type TranslationKey =
  | 'brand.name'
  | 'language.switchToChinese'
  | 'language.switchToEnglish'
  | 'products.found'
  | 'products.search'
  | 'products.searchPlaceholder'
  | 'account.username'
  | 'account.password'
  | 'account.signIn'
  | 'account.signOut'
  | 'account.signInFailed'
  | 'account.catalogue'
  | 'account.favourites'
  | 'account.orderHistory'
  | 'account.viewDetails'
  | 'account.backToOrders'
  | 'account.orderDetails'
  | 'account.unknownCategory'
  | 'account.reorder'
  | 'account.restaurantOnly'
  | 'account.noFavourites'
  | 'account.noOrders'
  | 'account.loading'
  | 'filter.categories'
  | 'product.freeShipping'
  | 'product.image'
  | 'product.installment'
  | 'product.priceUnavailable'
  | 'product.addToCart'
  | 'cart.shoppingCart'
  | 'cart.open'
  | 'cart.close'
  | 'cart.quantityTitle'
  | 'cart.title'
  | 'cart.subtotal'
  | 'cart.subtotalLabel'
  | 'cart.restaurantName'
  | 'cart.restaurantNamePlaceholder'
  | 'cart.phoneNumber'
  | 'cart.phoneNumberPlaceholder'
  | 'cart.orderNote'
  | 'cart.orderNotePlaceholder'
  | 'cart.checkout'
  | 'cart.checkoutDetails'
  | 'cart.backToCart'
  | 'cart.submitOrder'
  | 'cart.restaurantNameRequired'
  | 'cart.empty'
  | 'cart.quantity'
  | 'cart.removeProduct'
  | 'cart.checkoutAlert'
  | 'cart.emptyAlert'
  | 'cart.orderSuccess'
  | 'cart.orderFailure'
  | 'cart.installments';

type TranslationParams = Record<string, string | number>;

interface II18nContext {
  locale: Locale;
  setLocale(locale: Locale): void;
  t(key: TranslationKey, params?: TranslationParams): string;
}

const translations: Record<Locale, Record<TranslationKey, string>> = {
  en: {
    'brand.name': 'Jiahe Asian Market',
    'language.switchToChinese': 'Switch to Chinese',
    'language.switchToEnglish': 'Switch to English',
    'products.found': '{count} Product(s) found',
    'products.search': 'Search products',
    'products.searchPlaceholder': 'Search by product or category',
    'account.username': 'Username or email',
    'account.password': 'Password',
    'account.signIn': 'Sign in',
    'account.signOut': 'Sign out',
    'account.signInFailed': 'Could not sign in.',
    'account.catalogue': 'Catalogue',
    'account.favourites': 'Favourites',
    'account.orderHistory': 'Order history',
    'account.viewDetails': 'View details',
    'account.backToOrders': 'Back to orders',
    'account.orderDetails': 'Order #{id}',
    'account.unknownCategory': 'Items',
    'account.reorder': 'Reorder',
    'account.restaurantOnly': 'Restaurant account required',
    'account.noFavourites': 'No favourite products yet.',
    'account.noOrders': 'No orders yet.',
    'account.loading': 'Loading...',
    'filter.categories': 'Categories:',
    'product.freeShipping': 'Free shipping',
    'product.image': 'Product image',
    'product.installment': 'or {installments} x',
    'product.priceUnavailable': 'Price unavailable',
    'product.addToCart': 'Add to cart',
    'cart.shoppingCart': 'Shopping cart',
    'cart.open': 'Open cart',
    'cart.close': 'Close cart',
    'cart.quantityTitle': 'Products in cart quantity',
    'cart.title': 'Cart',
    'cart.subtotal': 'Subtotal',
    'cart.subtotalLabel': 'Cart subtotal',
    'cart.restaurantName': 'Restaurant name',
    'cart.restaurantNamePlaceholder': 'Enter restaurant name',
    'cart.phoneNumber': 'Phone number',
    'cart.phoneNumberPlaceholder': 'Enter phone number',
    'cart.orderNote': 'Order note',
    'cart.orderNotePlaceholder': 'Add delivery notes or order details',
    'cart.checkout': 'Checkout',
    'cart.checkoutDetails': 'Checkout details',
    'cart.backToCart': 'Back to cart',
    'cart.submitOrder': 'Submit order',
    'cart.restaurantNameRequired': 'Enter restaurant name before checkout.',
    'cart.empty': 'Add products to start an order.',
    'cart.quantity': 'Quantity',
    'cart.removeProduct': 'remove product from cart',
    'cart.checkoutAlert': 'Checkout - Subtotal: {amount}',
    'cart.emptyAlert': 'Add some product in the cart!',
    'cart.orderSuccess': 'Order submitted.',
    'cart.orderFailure': 'Could not submit order. Please try again.',
    'cart.installments': 'OR UP TO {installments} x {amount}',
  },
  zh: {
    'brand.name': '嘉禾亚洲超市',
    'language.switchToChinese': '切换到中文',
    'language.switchToEnglish': '切换到英文',
    'products.found': '共 {count} 件商品',
    'products.search': '搜索商品',
    'products.searchPlaceholder': '按商品或分类搜索',
    'account.username': '用户名或邮箱',
    'account.password': '密码',
    'account.signIn': '登录',
    'account.signOut': '退出登录',
    'account.signInFailed': '登录失败。',
    'account.catalogue': '商品目录',
    'account.favourites': '收藏商品',
    'account.orderHistory': '订单历史',
    'account.viewDetails': '查看详情',
    'account.backToOrders': '返回订单列表',
    'account.orderDetails': '订单 #{id}',
    'account.unknownCategory': '商品',
    'account.reorder': '再次下单',
    'account.restaurantOnly': '需要餐厅账号',
    'account.noFavourites': '暂无收藏商品。',
    'account.noOrders': '暂无订单。',
    'account.loading': '加载中...',
    'filter.categories': '分类：',
    'product.freeShipping': '免运费',
    'product.image': '商品图片',
    'product.installment': '或 {installments} 期',
    'product.priceUnavailable': '价格待定',
    'product.addToCart': '加入购物车',
    'cart.shoppingCart': '购物车',
    'cart.open': '打开购物车',
    'cart.close': '关闭购物车',
    'cart.quantityTitle': '购物车商品数量',
    'cart.title': '购物车',
    'cart.subtotal': '小计',
    'cart.subtotalLabel': '购物车小计',
    'cart.restaurantName': '餐厅名称',
    'cart.restaurantNamePlaceholder': '请输入餐厅名称',
    'cart.phoneNumber': '电话号码',
    'cart.phoneNumberPlaceholder': '请输入电话号码',
    'cart.orderNote': '订单备注',
    'cart.orderNotePlaceholder': '请输入配送备注或订单要求',
    'cart.checkout': '结账',
    'cart.checkoutDetails': '结账信息',
    'cart.backToCart': '返回购物车',
    'cart.submitOrder': '提交订单',
    'cart.restaurantNameRequired': '结账前请输入餐厅名称。',
    'cart.empty': '添加商品后开始下单。',
    'cart.quantity': '数量',
    'cart.removeProduct': '从购物车移除商品',
    'cart.checkoutAlert': '结账 - 小计：{amount}',
    'cart.emptyAlert': '请先添加商品到购物车！',
    'cart.orderSuccess': '订单已提交。',
    'cart.orderFailure': '订单提交失败，请重试。',
    'cart.installments': '最多 {installments} 期，每期 {amount}',
  },
};

const I18nContext = createContext<II18nContext | undefined>(undefined);

const interpolate = (message: string, params: TranslationParams = {}) =>
  Object.entries(params).reduce(
    (result, [key, value]) => result.replace(`{${key}}`, String(value)),
    message
  );

const I18nProvider: FC = (props) => {
  const [locale, setLocale] = useState<Locale>('en');

  const t = (key: TranslationKey, params?: TranslationParams) =>
    interpolate(translations[locale][key], params);

  return (
    <I18nContext.Provider value={{ locale, setLocale, t }} {...props} />
  );
};

const useI18n = () => {
  const context = useContext(I18nContext);

  if (!context) {
    throw new Error('useI18n must be used within an I18nProvider');
  }

  return context;
};

export { I18nProvider, useI18n };
export type { Locale, TranslationKey };
